/**
 * Zero-Knowledge Data Encryption Wrapper
 *
 * This utility provides simple functions to encrypt and decrypt user data
 * using the master key stored in sessionStorage. It automatically handles
 * retrieving the master key and provides clear error messages.
 */

import {
  encryptWithMasterKey,
  decryptWithMasterKey,
  encryptJSON,
  decryptJSON,
  encryptWithMasterKeyAsync,
  decryptWithMasterKeyAsync,
  encryptJSONAsync,
  decryptJSONAsync,
  retrieveMasterKey
} from './zkEncryption';

/**
 * Check if master key is available
 * @returns {boolean}
 */
export const isMasterKeyAvailable = () => {
  const masterKey = retrieveMasterKey();
  return !!masterKey;
};

/**
 * Encrypt user data (string or object)
 * @param {string|object} data - Data to encrypt
 * @returns {object} Encrypted data { ciphertext, iv, salt }
 * @throws {Error} If master key not found or encryption fails
 */
export const encryptUserData = (data) => {
  const masterKey = retrieveMasterKey();

  if (!masterKey) {
    throw new Error('Master key not found. Please log in again.');
  }

  try {
    // If data is an object, convert to JSON first
    if (typeof data === 'object') {
      return encryptJSON(data, masterKey);
    }

    // Otherwise encrypt as string
    return encryptWithMasterKey(data, masterKey);
  } catch (error) {
    throw new Error('Failed to encrypt data. Please try again.');
  }
};

/**
 * Decrypt user data
 * @param {object} encryptedData - Encrypted data { ciphertext, iv, salt }
 * @param {boolean} parseJSON - Whether to parse as JSON (default: true)
 * @returns {string|object} Decrypted data
 * @throws {Error} If master key not found or decryption fails
 */
export const decryptUserData = (encryptedData, parseJSON = true) => {
  const masterKey = retrieveMasterKey();

  if (!masterKey) {
    throw new Error('Master key not found. Please log in again.');
  }

  try {
    if (parseJSON) {
      return decryptJSON(encryptedData, masterKey);
    }

    return decryptWithMasterKey(encryptedData, masterKey);
  } catch (error) {
    throw new Error('Failed to decrypt data. Data may be corrupted or key is invalid.');
  }
};

/**
 * Encrypt user data using the fast native Web Crypto path (async).
 * Format-identical to encryptUserData; prefer this in hot paths.
 * @param {string|object} data
 * @returns {Promise<object>} { ciphertext, iv, salt }
 */
export const encryptUserDataAsync = async (data) => {
  const masterKey = retrieveMasterKey();
  if (!masterKey) {
    throw new Error('Master key not found. Please log in again.');
  }
  try {
    if (typeof data === 'object') {
      return await encryptJSONAsync(data, masterKey);
    }
    return await encryptWithMasterKeyAsync(data, masterKey);
  } catch (error) {
    throw new Error('Failed to encrypt data. Please try again.');
  }
};

/**
 * Decrypt user data using the fast native Web Crypto path (async).
 * @param {object} encryptedData - { ciphertext, iv, salt }
 * @param {boolean} parseJSON
 * @returns {Promise<string|object>}
 */
export const decryptUserDataAsync = async (encryptedData, parseJSON = true) => {
  const masterKey = retrieveMasterKey();
  if (!masterKey) {
    throw new Error('Master key not found. Please log in again.');
  }
  try {
    if (parseJSON) {
      return await decryptJSONAsync(encryptedData, masterKey);
    }
    return await decryptWithMasterKeyAsync(encryptedData, masterKey);
  } catch (error) {
    throw new Error('Failed to decrypt data. Data may be corrupted or key is invalid.');
  }
};

// Re-export core encryption functions from zkEncryption for convenience
export { encryptJSON, decryptJSON, encryptJSONAsync, decryptJSONAsync };

// Export all functions
export default {
  isMasterKeyAvailable,
  encryptUserData,
  decryptUserData,
  encryptUserDataAsync,
  decryptUserDataAsync,
  encryptJSON,
  decryptJSON
};
